import { RouterRootState } from 'connected-react-router';
import { useField, useFormikContext } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import arrowIcon from '../../images/Down Arrow.svg';
import { FingerIndex, Fingers } from '../../constants/biometric-constant';
import { Finger } from '../../models/Finger';
import { FormatOptions } from '../../models/FormatOptions';
import { ListQuery } from '../../models/ListQuery';
import { Name } from '../../models/Name';
import { PaginationModel } from '../../models/Pagination';
import statusOptions from '../../models/TagStatusOptions';
import { getAccessAreaNamesWithPagination } from '../../services/accessAreaService';
import { getAccessTimePatternNames } from '../../services/accessTimePatternService';
import { getBiometricNamesWithCurrentSelectedDevice } from '../../services/biometricService';
import { getBuildingNamesWithPagination } from '../../services/buildingService';
import { getCommonZoneNamesWithPagination } from '../../services/commonZoneService';
import { getCompanyNamesWithPagination } from '../../services/companyService';
import { getControllerNames } from '../../services/controllerService';
import { getCredentialNameTypesWithPagination, getMasterCredentialsWithPagination } from '../../services/credentialsService';
import { getDepartmentNamesWithPagination } from '../../services/departmentService';
import { getDeviceActionNamesForDoorWithPagination } from '../../services/deviceActionService';
import { getDeviceEventActionTypeNamesWithPagination } from '../../services/deviceEventActionService';
import { getDeviceImproxNames } from '../../services/deviceImproxService';
import { getDeviceTimePatternNames } from '../../services/deviceTimePatternService';
import { getEnrolmentNames } from '../../services/enrolmentService';
import { getEventTypeNamesWithPagination } from '../../services/eventTypeService';
import { getDeviceProfileNames } from '../../services/hardwarePresetService';
import { getLocationTypeNames } from '../../services/locationTypeService';
import { getMessageNamesWithPagination } from '../../services/messageService';
import { getModeTypeNames } from '../../services/modeTypeService';
import { getPermissionNames } from '../../services/permissionService';
import { getPrintTemplatesWithPagination } from '../../services/printTemplateService';
import { getModuleProfilesNames } from '../../services/profileService';
import { getThreatLevelNames } from '../../services/threatLevelService';
import { getTimeZones } from '../../services/timeZoneService';
import { showError } from '../../services/utilities/ServiceError';
import { getZoneNamesWithPagination, getZoneNamesWithPaginationExcluding } from '../../services/zoneService';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import { addCustomFieldMetaData } from '../../utilities/formFields';
import { generateFieldId } from '../../utilities/GenerateFieldId';
import { requiredValidation } from '../../utilities/Validation';
import Card from '../card/card';
import Label from '../label/label';
import OutsideClick from '../outside-click/outside-click';
import PaginatedSearchableList from '../searchable-list/paginatedSearchableList2';
import { toast } from "react-toastify";
import { useWizardSummaryContext } from '../wizard/wizardSummaryContext';
import { PRESET_TYPES } from '../../constants/preset-constants';

export enum SelectType {
    gender = "gender",
    printTemplate = "printTemplate",
    company = "company",
    department = "department",
    event = "event",
    reader = "reader",
    zone = "zone",
    message = "message",
    emailAddress = "emailAddress",
    contactNumber = "contactNumber",
    presetInputTemplate = "presetInputTemplate",
    presetActionTemplate = "presetActionTemplate",
    presetParameterTemplate = "presetParameterTemplate",
    deviceTimePattern = "deviceTimePattern",
    controllerProfile = "controllerProfile",
    readerProfile = "readerProfile",
    ioDeviceProfile = "ioDeviceProfile",
    lockProfile = "lockProfile",
    lockerProfile = "lockerProfile",
    bioMetricProfile = "bioMetricProfile",
    enrolments = "enrolments",
    permissions = "permissions",
    accessArea = "accessArea",
    timeZone = "timeZone",
    building = "building",
    floor = "floor",
    accessTimePattern = "accessTimePattern",
    systemController = "systemController",
    reportType = "reportType",
    deviceImprox = "deviceImprox",
    deviceAction = "deviceAction",
    deviceEventAction = "deviceEventAction",
    readerModes = "readerModes",
    readerFunction = "readerFunction",
    threatLevel = "threatLevel",
    moduleProfiles = "moduleProfiles",
    credentialsTypes = "credentialsTypes",
    credentialStatuses = "credentialStatuses",
    biometricInput = "biometricInput",
    formatSelector = "formatSelector",
    handEnrollTypes = "handEnrollTypes",
    cardSelector = "cardSelector",
    fingerSelector = "fingerSelector",
    zoneExcluding = "zoneExcluding",
    commonZone = "commonZone"
}

interface SettingSelectFieldProps {
    newPageSize?: number
    accessor: string;
    profileId?: string;
    displayName?: string;
    required?: boolean;
    options?: Name[];
    selectType?: SelectType;
    detailTypeId?: number;
    onSelect?: (item) => void;
    className?: string;
    isNonFormSelect?: boolean;
    clearSelectedOption?: boolean;
    setClearSelectedOption?: any,
    updateSelect?: boolean;
    setUpdateSelect?: any;
    customFilterQuery?: {
        fieldValue: string,
        fieldLabel: string,
        fieldId: string

    };
    devicePostion?: boolean;
    deviceImproxId?: number;
    deviceProfileSubType?: string;
    itemSiteId?: number;
    disabled?: boolean;
    width?: string;
}

const emptyArray = new Array(0);

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

/**
 * Custom select must pass options through attribute, else the component defaults to gender options.
 * @param param0
 * @returns
 */
const SettingSelectField: FC<SettingSelectFieldProps> = (
    { newPageSize,accessor, displayName, required = false, options = undefined, selectType, detailTypeId = undefined, onSelect = () => { },
        className = 'pr-6 w-full', isNonFormSelect, clearSelectedOption, setClearSelectedOption, customFilterQuery, updateSelect, setUpdateSelect,
        devicePostion, itemSiteId = undefined, deviceImproxId, disabled = false, width }) => {
    const pageSize = newPageSize || 5
    const [fieldOptions, setFieldOptions] = useState<any>(emptyArray);
    const [pagination, setPagination] = useState<PaginationModel>({
        totalCount: options?.length ?? emptyArray.length,
        maxPageSize: pageSize,
        totalPages: 1,
        pageNumber: 1
    });
    const [listQuery, setListQuery] = useState<ListQuery>({
        goToPage: 1,
        pageSize: pageSize,
    })
    const [open, setOpen] = useState(false);
    const initialOption = { value: '', label: '' };
    const [selectedOption, setSelectedOption] = useState(initialOption);
    const [selectedItems, setSelectedItems] = useState(new Array(0));
    const { translate } = useLocale();
    const { values, errors, setFieldValue } = useFormikContext();
    const itemId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [field] = useField({ name: accessor, validate: required ? (value) => requiredValidation(value, translate) : undefined });

    const siteId = useSelector((state: IAppState) => (state.site.site.value));
    const { updateSummary } = useWizardSummaryContext();
    const [searchable, setSearchable] = useState(true);
    const genderOptions = [
        { value: 'M', label: translate.byKey(TranslationKeys.male) },
        { value: 'F', label: translate.byKey(TranslationKeys.female) },
        { value: 'U', label: translate.byKey(TranslationKeys.unspecified) }
    ];
    const handOptions = [
        { label: translate.byKey(TranslationKeys.leftHand), value: "2" },
        { label: translate.byKey(TranslationKeys.rightHand), value: "3" },
        { label: translate.byKey(TranslationKeys.both), value: "4" }
    ];
    const fingerOptions:Finger[] = [
        { label: translate.byKey(TranslationKeys.leftLittle), value: Fingers.LEFT_LITTLE, index: FingerIndex.LEFT_LITTLE },
        { label: translate.byKey(TranslationKeys.leftRing), value: Fingers.LEFT_RING, index: FingerIndex.LEFT_RING },
        { label: translate.byKey(TranslationKeys.leftMiddle), value: Fingers.LEFT_MIDDLE, index: FingerIndex.LEFT_MIDDLE },
        { label: translate.byKey(TranslationKeys.leftIndex), value: Fingers.LEFT_INDEX, index: FingerIndex.LEFT_INDEX },
        { label: translate.byKey(TranslationKeys.leftThumb), value: Fingers.LEFT_THUMB, index: FingerIndex.LEFT_THUMB },
        { label: translate.byKey(TranslationKeys.rightThumb), value: Fingers.RIGHT_THUMB, index: FingerIndex.RIGHT_THUMB },
        { label: translate.byKey(TranslationKeys.rightIndex), value: Fingers.RIGHT_INDEX, index: FingerIndex.RIGHT_INDEX },
        { label: translate.byKey(TranslationKeys.rightMiddle), value: Fingers.RIGHT_MIDDLE, index: FingerIndex.RIGHT_MIDDLE },
        { label: translate.byKey(TranslationKeys.rightRing), value: Fingers.RIGHT_RING, index: FingerIndex.RIGHT_RING },
        { label: translate.byKey(TranslationKeys.rightLittle), value: Fingers.RIGHT_LITTLE, index: FingerIndex.RIGHT_LITTLE }];

    const addSiteFilter = (newSiteId, filters) => {
        if (newSiteId) {
            const currentSiteFilter = {
                label: 'Site',
                key: 'site.id',
                options: [{ label: newSiteId, value: newSiteId }],
                selected: [{ label: newSiteId, value: newSiteId }]
            };
            const siteSpecifiedSelectedFilters = filters
                ? filters.concat([currentSiteFilter])
                : [currentSiteFilter];
            return siteSpecifiedSelectedFilters;
        }
        else {
            return filters;
        }
    }

    const setupFieldOptions = async (dataMethod: (listParameters?: ListQuery | undefined) => Promise<{ rows: Name[]; pagination: PaginationModel; }>, newListQuery: ListQuery) => {
        try {
            let tempPagination = pagination;
            let newFieldOptions = new Array(0);
            do {
                const newOptions = await dataMethod({ ...newListQuery, goToPage: tempPagination.pageNumber, pageSize: tempPagination.maxPageSize }); // Was 100 which affects all fields. E.g. Time Triggered Actions > action field
                newFieldOptions = newFieldOptions.concat(newOptions.rows);
                tempPagination = { ...newOptions.pagination, pageNumber: newOptions.pagination.pageNumber + 1 };
            } while (tempPagination.pageNumber <= tempPagination.totalPages);
            setFieldOptions(newFieldOptions);
            setPagination({ ...pagination, maxPageSize: pageSize, totalPages: tempPagination.totalCount / pageSize, totalCount: tempPagination.totalCount });
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const generateCustomFilter = (newListQuery) => {
        return { ...newListQuery, filters: appendFilter(customFilterQuery ? customFilterQuery?.fieldValue : "", customFilterQuery ? customFilterQuery?.fieldLabel : "", customFilterQuery ? customFilterQuery?.fieldId : "", newListQuery?.filters ?? []) };
    }

    const refreshOptions = async (newListQuery) => {
        const siteUpdatedListQuery = { ...newListQuery, filters: addSiteFilter(itemSiteId ?? siteId, newListQuery?.filters) }
        if (!options || options.length === 0) {
            let tempPagination = pagination;
            let newFieldOptions = new Array(0);
            switch (selectType) { // consider moving to dictionary
                case SelectType.printTemplate:
                    setupFieldOptions(getPrintTemplatesWithPagination, siteUpdatedListQuery);
                    break;
                case SelectType.company:
                    setupFieldOptions(getCompanyNamesWithPagination, siteUpdatedListQuery);
                    break;
                case SelectType.department:
                    setupFieldOptions(getDepartmentNamesWithPagination, siteUpdatedListQuery);
                    break;
                case SelectType.zone:
                    setupFieldOptions(getZoneNamesWithPagination, newListQuery);
                    break;
                case SelectType.gender:
                    setSearchable(false);
                    setFieldOptions(genderOptions);
                    setPagination({ pageNumber: 1, maxPageSize: genderOptions.length, totalPages: 1, totalCount: genderOptions.length });
                    break;
                case SelectType.message:
                    setupFieldOptions(getMessageNamesWithPagination, newListQuery);
                    break;
                case SelectType.event:
                    setupFieldOptions(getEventTypeNamesWithPagination, newListQuery);
                    break;
                case SelectType.deviceTimePattern:
                    setupFieldOptions(getDeviceTimePatternNames, siteUpdatedListQuery);
                    break;
                case SelectType.controllerProfile:
                    setupFieldOptions((listParameters) => getDeviceProfileNames(PRESET_TYPES.CONTROLLER, listParameters), siteUpdatedListQuery);
                    break;
                case SelectType.readerProfile:
                    setupFieldOptions((listParameters) => getDeviceProfileNames(PRESET_TYPES.READER, listParameters), siteUpdatedListQuery);
                    break;
                case SelectType.ioDeviceProfile:
                    setupFieldOptions((listParameters) => getDeviceProfileNames(PRESET_TYPES.IOMODULE, listParameters), siteUpdatedListQuery);
                    break;
                case SelectType.enrolments:
                    setupFieldOptions(getEnrolmentNames, siteUpdatedListQuery);
                    break;
                case SelectType.permissions:
                    const permissionUpdatedListQuery = { ...newListQuery };
                    setupFieldOptions(getPermissionNames, permissionUpdatedListQuery);
                    break;
                case SelectType.accessArea:
                    setupFieldOptions(() => getAccessAreaNamesWithPagination(null), siteUpdatedListQuery);
                    break;
                case SelectType.accessTimePattern:
                    setupFieldOptions(getAccessTimePatternNames, siteUpdatedListQuery);
                    break;
                case SelectType.building:
                    setupFieldOptions(getBuildingNamesWithPagination, siteUpdatedListQuery);
                    break;
                case SelectType.timeZone:
                    setupFieldOptions(getTimeZones, newListQuery);
                    break;
                case SelectType.systemController:
                    setupFieldOptions(getControllerNames, siteUpdatedListQuery);
                    break;
                case SelectType.deviceImprox:
                    setupFieldOptions(getDeviceImproxNames, siteUpdatedListQuery);
                    break;
                case SelectType.deviceAction:
                    setupFieldOptions(getDeviceActionNamesForDoorWithPagination, customFilterQuery ? generateCustomFilter(newListQuery) : siteUpdatedListQuery);
                    break;
                case SelectType.deviceEventAction:
                    setupFieldOptions((listQuery) => getDeviceEventActionTypeNamesWithPagination(devicePostion??false, listQuery), newListQuery);
                    break;
                case SelectType.readerModes:
                    setupFieldOptions(getModeTypeNames, newListQuery);
                    break;
                case SelectType.readerFunction:
                    setupFieldOptions(getLocationTypeNames, newListQuery);
                    break;
                case SelectType.threatLevel:
                    setupFieldOptions(getThreatLevelNames, newListQuery);
                    break;
                case SelectType.moduleProfiles:
                    setupFieldOptions(getModuleProfilesNames, siteUpdatedListQuery);
                    break;
                case SelectType.credentialsTypes:
                    setupFieldOptions(getCredentialNameTypesWithPagination, newListQuery);
                    break;
                case SelectType.credentialStatuses:
                    const tagStatusOptions = statusOptions(translate);
                    setSearchable(false);
                    setFieldOptions(tagStatusOptions);
                    setPagination({ pageNumber: 1, maxPageSize: tagStatusOptions.length, totalPages: 1, totalCount: tagStatusOptions.length });
                    break;
                case SelectType.biometricInput:
                    setupFieldOptions((listQuery) => getBiometricNamesWithCurrentSelectedDevice(listQuery, deviceImproxId), newListQuery);
                    break;
                case SelectType.formatSelector:
                    setSearchable(false);
                    setFieldOptions(FormatOptions);
                    setPagination({ pageNumber: 1, maxPageSize: FormatOptions.length, totalPages: 1, totalCount: FormatOptions.length });
                    break;
                case SelectType.handEnrollTypes:
                    setFieldOptions(handOptions);
                    setSearchable(false);
                    break;
                case SelectType.cardSelector:
                    const newListParameters = { ...newListQuery, filters: appendFilter(itemId, 'Master', 'master.id', newListQuery?.filters ?? []) };
                    setupFieldOptions(getMasterCredentialsWithPagination,newListParameters);
                    break;
                case SelectType.fingerSelector:
                    setFieldOptions(fingerOptions);
                    setSearchable(false);
                    break;
                case SelectType.zoneExcluding:
                    setupFieldOptions((listQuery) => getZoneNamesWithPaginationExcluding(itemId, listQuery), newListQuery);
                    break
                case SelectType.commonZone:
                    setupFieldOptions(getCommonZoneNamesWithPagination, newListQuery);
                    break
                default:
                    break;
            }
        }
        else {
            setPagination({ ...pagination, pageNumber: newListQuery.goToPage })
        }
    }

    useEffect(() => {
        const selectedValue = accessor.split('.').reduce((previous, current) => {
            return previous?.[current]
        }, values as any);
        // find the index of the selected item in the currently displayed list of items.
        const newSelectedOptionIndex = fieldOptions
            .findIndex(option => selectedValue === option.value);
        if (!!selectedValue && newSelectedOptionIndex >= 0) {
            setSelectedItems(newSelectedOptionIndex >= 0 ? [newSelectedOptionIndex] : []);
            const newSelectedOption = fieldOptions.find(option => selectedValue === (option.value)) ?? initialOption;
            setSelectedOption(newSelectedOption);

            updateSummary(accessor, newSelectedOption.label);
        }
        else {
            setSelectedItems([]);
            setSelectedOption(initialOption);
            updateSummary(accessor, "");
        }
    }, [values, fieldOptions])

    useEffect(() => {
        addCustomFieldMetaData(detailTypeId, values, accessor, setFieldValue);

        refreshOptions(listQuery);
    }, [itemSiteId === undefined, siteId !== itemSiteId, siteId]);

    useEffect(() => {
        refreshOptions(listQuery);
    }, [deviceImproxId, itemId]);

    useEffect(() => {
        if (options) { setFieldOptions(options); }
    }, [options]);

    useEffect(() => {
        if (clearSelectedOption) {
            setSelectedOption(initialOption);
            setFieldValue(accessor, '');
            setClearSelectedOption(false);
            setSelectedItems([]);
        }
    }, [clearSelectedOption])

    useEffect(() => {
        if (updateSelect) {
            refreshOptions(listQuery);
            setUpdateSelect(false);
        }
    }, [updateSelect])

    const handleItemSelected = (index, item) => {
        const internalItem = fieldOptions[index];
        if (internalItem) {
            setFieldValue(accessor, internalItem.value)
            if (selectType === SelectType.presetActionTemplate || selectType === SelectType.presetInputTemplate || selectType === SelectType.presetParameterTemplate) {
                setFieldValue('profileId', internalItem.profileId);
                setFieldValue('sdist', internalItem.sdist);
                setFieldValue('settingIdToCopy', internalItem.settingIdToCopy)
            }
            setSelectedOption(internalItem);
            setSelectedItems([index]);
            onSelect(internalItem);

            updateSummary(accessor, internalItem.label);
        }
        setOpen(false);
    };


    return <div
        className={`flex flex-col mb-2 ${className}`}
        id={generateFieldId(accessor, 'SelectFieldLabel')}>
        {displayName ? <div className="flex flex-row justify-between" id={generateFieldId(accessor, 'TextFieldLabel')}>
            <Label
                htmlFor={accessor}
                className="text-xs text-gray-600 mt-1"
                required={required}>{displayName}:</Label>
            {(() => {
                const error = accessor.split('.').reduce((previous, current) => { return previous?.[current] }
                    , errors as any);
                if (error) {
                    return <span className='text-red-600 text-xs'>{error}</span>
                } else {
                    return null;
                }
            })()}
        </div> : null}
        <div
            id={generateFieldId(accessor, 'SelectField')} className={`${width ? width : ''}`}>
            <OutsideClick
                onClick={() => setOpen(false)}>
                <button onClick={() => setOpen(!open)} type="button" disabled={disabled} className={`bg-gray-input p-1 px-2 text-left flex flex-row justify-between text-sm  ${width ? width : 'w-full min-width-68'}  ${disabled ? 'cursor-not-allowed text-gray-500' : 'text-gray-900'}`}>
                    {selectedOption.label || '-'}
                    <img className="pl-2 select-field--arrow" src={arrowIcon} alt=""></img>
                </button>
                <div className={`${isNonFormSelect ? '' : "relative"} ${width ? width : ''} `}>
                    <Card
                        className={`bg-white px-2 py-2 ${width ? width : 'selection-content-list'}  ${open ? '' : 'hidden'}`}
                        style={{ position: 'absolute', zIndex: 999999 }}>

                        <PaginatedSearchableList
                            searchBoxClassName="mb-4"
                            titleClassName="mb-0"
                            stripedListClassName="mb-2"
                            stripedListItemClassName="p-1 pl-3"
                            selectedListItemClassName="p-1 pl-1-5 border-l-7 border-portal-blue-primary bg-selection-highlight border-t-selection-highlight border-r-selection-highlight border-b-selection-highlight"
                            items={fieldOptions}
                            onSelect={(index, item) => handleItemSelected(index, item)}
                            selectedItems={selectedItems}
                            title={''}
                            pagination={pagination}
                            setPagination={setPagination}
                            searchable={searchable}>
                        </PaginatedSearchableList>
                    </Card>
                </div>
            </OutsideClick>
        </div>
    </div>;
};

export default SettingSelectField

